<script setup>
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { useWindowSize, useElementBounding } from '@vueuse/core';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';

const props = defineProps({
  option: {
    type: Object,
    default: () => {},
  },
  subMenuAvailable: {
    type: Boolean,
    default: true,
  },
  submenuClass: {
    type: [String, Array, Object],
    default: '',
  },
  submenuEstimatedHeight: {
    type: Number,
    default: 240,
  },
});

const menuRef = useTemplateRef('menuRef');
const isHovered = ref(false);
const isPinnedOpen = ref(false);
const submenuId = `submenu-${Math.random().toString(36).slice(2)}`;
const { width: windowWidth, height: windowHeight } = useWindowSize();
const { bottom, right } = useElementBounding(menuRef);

// Vertical position
const verticalPosition = computed(() => {
  const spaceBelow = windowHeight.value - bottom.value;
  return spaceBelow < props.submenuEstimatedHeight ? 'bottom-0' : 'top-0';
});

// Horizontal position
const horizontalPosition = computed(() => {
  const SUBMENU_WIDTH = 240;
  const spaceRight = windowWidth.value - right.value;
  return spaceRight < SUBMENU_WIDTH ? 'right-full' : 'left-full';
});

const submenuPosition = computed(() => [
  verticalPosition.value,
  horizontalPosition.value,
]);

const shouldShowSubmenu = computed(
  () => props.subMenuAvailable && (isHovered.value || isPinnedOpen.value)
);

const onMouseEnter = () => {
  isHovered.value = true;
  isPinnedOpen.value = true;
  window.dispatchEvent(
    new CustomEvent('conversation-context-submenu-open', {
      detail: { id: submenuId },
    })
  );
};

const onMouseLeave = () => {
  isHovered.value = false;
};

const closeSubmenu = () => {
  isHovered.value = false;
  isPinnedOpen.value = false;
};

const onWindowPointerDown = event => {
  if (!menuRef.value?.contains(event.target)) {
    closeSubmenu();
  }
};

const onSubmenuOpen = event => {
  if (event.detail?.id !== submenuId) {
    closeSubmenu();
  }
};

onMounted(() => {
  window.addEventListener('mousedown', onWindowPointerDown);
  window.addEventListener('conversation-context-submenu-open', onSubmenuOpen);
});

onBeforeUnmount(() => {
  window.removeEventListener('mousedown', onWindowPointerDown);
  window.removeEventListener(
    'conversation-context-submenu-open',
    onSubmenuOpen
  );
});
</script>

<template>
  <div
    ref="menuRef"
    class="menu-with-submenu relative flex h-10 w-full items-center justify-between rounded-xl px-3 py-2 text-n-slate-12 cursor-pointer transition-colors hover:bg-n-alpha-2"
    :class="!subMenuAvailable ? 'opacity-50 cursor-not-allowed' : ''"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="flex items-center gap-2">
      <HugeIcon
        v-if="option.hugeIcon"
        :icon="option.hugeIcon"
        :size="20"
        class="text-n-slate-11"
      />
      <fluent-icon
        v-else
        :icon="option.icon"
        size="20"
        class="text-n-slate-11"
      />
      <p class="m-0 text-sm font-medium">{{ option.label }}</p>
    </div>
    <fluent-icon icon="chevron-right" size="14" class="text-n-slate-11" />
    <div
      v-show="shouldShowSubmenu"
      class="submenu absolute hidden max-h-[15rem] overflow-y-auto overflow-x-hidden rounded-2xl border border-n-weak bg-n-surface-1 p-1.5 shadow-xl cursor-pointer"
      :class="[submenuPosition, submenuClass]"
      @mousedown.stop
    >
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
.menu-with-submenu {
  min-width: 11.25rem;
}

.submenu {
  @apply block;
}
</style>
